/*
 * GALINUR — Hero slideshow
 * Dependency-free. Fade or slide transition, autoplay with pause rules,
 * pointer/swipe, keyboard, RTL-aware, respects prefers-reduced-motion.
 */
if (!customElements.get('hero-slideshow')) {
  class HeroSlideshow extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.hero-slideshow__track');
      this.slides = Array.from(this.querySelectorAll('.hero-slide'));
      this.dots = Array.from(this.querySelectorAll('.hero-slideshow__dot'));
      this.prevBtn = this.querySelector('.hero-slideshow__arrow--prev');
      this.nextBtn = this.querySelector('.hero-slideshow__arrow--next');
      this.status = this.querySelector('.hero-slideshow__status');

      this.count = this.slides.length;
      this.index = 0;
      this.mode = this.dataset.transition === 'slide' ? 'slide' : 'fade';
      this.autoplay = this.dataset.autoplay === 'true';
      this.delay = Math.max(3, parseInt(this.dataset.speed, 10) || 6) * 1000;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.timer = null;
      this._rtl = null;
    }

    connectedCallback() {
      if (this.count <= 1) return;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1, true));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i, true)));

      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.start();
      });
      this._onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this._onVisibility);

      this.bindPointer();
      this.goTo(0, false);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
    }

    get rtl() {
      if (this._rtl === null) this._rtl = getComputedStyle(this).direction === 'rtl';
      return this._rtl;
    }

    onKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.step(this.rtl ? 1 : -1, true);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.step(this.rtl ? -1 : 1, true);
      }
    }

    step(direction, fromUser) {
      this.goTo((this.index + direction + this.count) % this.count, fromUser);
    }

    goTo(i, fromUser) {
      this.index = i;

      this.slides.forEach((slide, n) => {
        const active = n === i;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a, button').forEach((el) => {
          el.tabIndex = active ? 0 : -1;
        });
      });

      this.dots.forEach((dot, n) => {
        const active = n === i;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        dot.tabIndex = active ? 0 : -1;
      });

      if (this.mode === 'slide') {
        const sign = this.rtl ? 1 : -1;
        this.track.style.transform = `translateX(${sign * i * 100}%)`;
      }

      if (this.status) this.status.textContent = `${i + 1} / ${this.count}`;

      if (fromUser) this.restart();
    }

    start() {
      if (this.timer || !this.autoplay || this.reduceMotion || this.count <= 1) return;
      this.timer = window.setInterval(() => this.step(1, false), this.delay);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    restart() {
      this.stop();
      this.start();
    }

    bindPointer() {
      let startX = null;
      let startY = null;

      this.track.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          startX = e.clientX;
          startY = e.clientY;
          this.stop();
        },
        { passive: true }
      );

      const end = (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        startX = null;
        startY = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          const movedLeft = dx < 0;
          const forward = this.rtl ? !movedLeft : movedLeft;
          this.step(forward ? 1 : -1, true);
        } else {
          this.start();
        }
      };

      this.track.addEventListener('pointerup', end, { passive: true });
      this.track.addEventListener('pointercancel', () => {
        startX = null;
        startY = null;
        this.start();
      });
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);
}
